import React, { useState, useRef, useEffect } from 'react';
import ReactCrop, { type Crop, centerCrop, makeAspectCrop } from 'react-image-crop';

interface ImageCropUploaderProps {
  onSave: (blob: Blob) => void;
  initialImageUrl?: string;
  aspect?: number;
}

function canvasPreview(
  image: HTMLImageElement,
  canvas: HTMLCanvasElement,
  crop: Crop
) {
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('No 2d context');
  }

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelRatio = window.devicePixelRatio || 1;

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio);
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio);

  ctx.scale(pixelRatio, pixelRatio);
  ctx.imageSmoothingQuality = 'high';

  const cropX = crop.x * scaleX;
  const cropY = crop.y * scaleY;

  const centerX = image.naturalWidth / 2;
  const centerY = image.naturalHeight / 2;

  ctx.save();
  ctx.translate(-cropX, -cropY);
  ctx.translate(centerX, centerY);
  ctx.translate(-centerX, -centerY);
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
    0,
    0,
    image.naturalWidth,
    image.naturalHeight,
  );
  ctx.restore();
}

const ImageCropUploader: React.FC<ImageCropUploaderProps> = ({ onSave, initialImageUrl, aspect = 1 }) => {
  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<Crop>();
  const [showCropper, setShowCropper] = useState(false);
  const [currentImage, setCurrentImage] = useState(initialImageUrl || '');

  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCurrentImage(initialImageUrl || '');
  }, [initialImageUrl]);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Makes crop preview update between images.
      const reader = new FileReader();
      reader.addEventListener('load', () => setImgSrc(reader.result?.toString() || ''));
      reader.readAsDataURL(e.target.files[0]);
      setShowCropper(true);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    const newCrop = centerCrop(
      makeAspectCrop(
        {
          unit: '%',
          width: 90,
        },
        aspect,
        width,
        height,
      ),
      width,
      height,
    );
    setCrop(newCrop);
    setCompletedCrop(newCrop);
  };

  const handleSaveCrop = () => {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist');
    }

    canvasPreview(image, previewCanvas, completedCrop);

    previewCanvas.toBlob(blob => {
      if (blob) {
        onSave(blob);
        const objectUrl = URL.createObjectURL(blob);
        setCurrentImage(objectUrl);
      }
      setShowCropper(false);
      setImgSrc('');
    }, 'image/png');
  };

  return (
    <div className="text-center">
      <label className="block text-sm font-medium text-gray-700 mb-1">Avatar Photo</label>
      <div className="relative w-48 h-48 mx-auto border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center bg-gray-50 overflow-hidden">
        {currentImage ? (
          <img src={currentImage} alt="Current avatar" className="w-full h-full object-cover" />
        ) : (
          <div className="text-center text-gray-500 p-2">
            <svg className="mx-auto h-8 w-8" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24" fill="none" stroke="currentColor" strokeWidth="1"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
            <span className="mt-2 text-xs">No image uploaded</span>
          </div>
        )}
      </div>
      <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-2 text-sm font-medium text-indigo-600 hover:text-indigo-500">
        {currentImage ? 'Change Image' : 'Upload Image'}
      </button>
      <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={onSelectFile} />
      
      {showCropper && (
        <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex justify-center items-center p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Crop Image</h3>
            {imgSrc && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                minWidth={100}
                minHeight={100}
              >
                <img ref={imgRef} alt="Crop me" src={imgSrc} onLoad={onImageLoad} className="max-h-[60vh]"/>
              </ReactCrop>
            )}
            <div className="mt-4 flex justify-end space-x-3">
              <button type="button" onClick={() => setShowCropper(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300">Cancel</button>
              <button type="button" onClick={handleSaveCrop} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700">Save Image</button>
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for preview */}
      <canvas ref={previewCanvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default ImageCropUploader;